import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { fetchCaseChatHistory, sendCaseChatMessage, fetchFileContent } from '../../api/cases';
import { CaseChatMessage } from '../../types';
import { formatTimestamp } from '../../utils/dateFormatters';

interface CaseChatProps {
  case_id: string;
}

const TOOL_ICONS: Record<string, string> = {
  get_data_inventory:          '📁',
  get_entities:                '👥',
  cash_activity_report:        '💰',
  bank_statement_gap_analysis: '🔍',
  search_transactions:         '🔎',
  analyze_transactions:        '🤖',
  create_file:                 '📄',
};

const TOOL_LABELS: Record<string, string> = {
  get_data_inventory:          'Get Data Inventory',
  get_entities:                'Get Entities',
  cash_activity_report:        'Cash Activity Report',
  bank_statement_gap_analysis: 'Gap Analysis',
  search_transactions:         'Search Transactions',
  analyze_transactions:        'Analyze Transactions (AI)',
  create_file:                 'Create File',
};

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

// ---------------------------------------------------------------------------
// Thinking step: an assistant message that only issued tool call(s), plus its
// paired tool result message(s). Rendered collapsed by default, like the
// agent's step trace.
// ---------------------------------------------------------------------------

interface ThinkingStepProps {
  caseId: string;
  toolCall: { id: string; function: { name: string; arguments: string } };
  result?: CaseChatMessage;
  isPending: boolean;
}

const ThinkingStep: React.FC<ThinkingStepProps> = ({ caseId, toolCall, result, isPending }) => {
  const [expanded, setExpanded] = useState(false);
  const auth = useAuth();
  const [downloading, setDownloading] = useState(false);
  const name = toolCall.function.name;
  const icon = TOOL_ICONS[name] ?? '🔧';
  const label = TOOL_LABELS[name] ?? name;

  let args: Record<string, any> = {};
  try { args = JSON.parse(toolCall.function.arguments || '{}'); } catch {}
  const argSummary = Object.keys(args).length > 0
    ? Object.entries(args).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(' · ')
    : null;

  const generatedFile = result?.generated_file;

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!generatedFile) return;
    setDownloading(true);
    try {
      const response = await fetchFileContent(caseId, generatedFile.file_id, auth.user?.id_token);
      const viewUrl = response.data?.view_url;
      if (!viewUrl) throw new Error('No download URL returned');
      window.open(viewUrl, '_blank');
    } catch (err: any) {
      alert(`Download failed: ${err.message}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="border rounded mb-2 border-secondary" style={{ fontSize: '0.8rem', maxWidth: 560 }}>
      <div
        className="d-flex align-items-center px-2 py-1 gap-2"
        style={{ cursor: 'pointer', background: '#f7f7f9' }}
        onClick={() => setExpanded(e => !e)}
      >
        <span>{icon}</span>
        <span className="fw-semibold flex-shrink-0">{label}</span>
        {argSummary && (
          <span className="text-muted" style={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {argSummary}
          </span>
        )}
        {isPending && !result && <Spinner animation="border" size="sm" className="ms-auto flex-shrink-0" />}
        <span className="text-muted ms-auto flex-shrink-0" style={{ fontSize: '0.75rem' }}>{expanded ? '▲' : '▼'}</span>
      </div>
      {expanded && (
        <div className="px-2 pb-2 border-top" style={{ background: '#fafafa' }}>
          {result?.content && (
            <div
              className="mt-2 p-2 rounded"
              style={{ background: '#eef', fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto', wordBreak: 'break-word' }}
            >
              {result.content}
            </div>
          )}
          {!result && <div className="mt-2 text-muted">Running…</div>}
        </div>
      )}
      {generatedFile && (
        <div className="px-2 pb-2">
          <Button variant="outline-primary" size="sm" onClick={handleDownload} disabled={downloading}>
            {downloading ? <Spinner size="sm" animation="border" /> : `⬇ ${generatedFile.filename}`}
          </Button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main chat component
// ---------------------------------------------------------------------------

const CaseChat: React.FC<CaseChatProps> = ({ case_id }) => {
  const auth = useAuth();
  const [messages, setMessages] = useState<CaseChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [waiting, setWaiting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollDeadlineRef = useRef<number>(0);
  const bottomRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async (): Promise<CaseChatMessage[]> => {
    const data = await fetchCaseChatHistory(case_id, auth.user?.id_token);
    const msgs = data.messages ?? [];
    setMessages(msgs);
    return msgs;
  }, [case_id, auth.user?.id_token]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await load();
        setError(null);
      } catch (e: any) {
        setError(e.message ?? 'Failed to load chat history');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setWaiting(false);
  }, []);

  const isFinalAssistantReply = (msgs: CaseChatMessage[], afterCount: number) => {
    if (msgs.length <= afterCount) return false;
    const last = msgs[msgs.length - 1];
    return last.role === 'assistant' && !!last.content && (!last.tool_calls || last.tool_calls.length === 0);
  };

  const startPolling = useCallback((countBeforeSend: number) => {
    setWaiting(true);
    pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const msgs = await load();
        if (isFinalAssistantReply(msgs, countBeforeSend)) {
          stopPolling();
        } else if (Date.now() > pollDeadlineRef.current) {
          stopPolling();
          setError('Response is taking longer than expected. Refresh to check for updates.');
        }
      } catch {
        // transient errors during polling are ignored; next tick retries
      }
    }, POLL_INTERVAL_MS);
  }, [load, stopPolling]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || waiting) return;
    setInput('');
    setError(null);
    const countBefore = messages.length;
    try {
      await sendCaseChatMessage(case_id, text, auth.user?.id_token);
      await load();
      startPolling(countBefore);
    } catch (e: any) {
      setError(e.message ?? 'Failed to send message');
      setInput(text);
    }
  };

  // Group into: user/final-assistant bubbles, and "thinking" tool-call groups.
  const renderMessages = () => {
    const out: React.ReactNode[] = [];
    for (let i = 0; i < messages.length; i++) {
      const m = messages[i];
      if (m.role === 'user') {
        out.push(
          <div key={m.message_id} className="d-flex justify-content-end mb-2">
            <div className="p-2 px-3 rounded" style={{ background: '#0d6efd', color: 'white', maxWidth: '75%', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {m.content}
            </div>
          </div>
        );
      } else if (m.role === 'assistant' && m.tool_calls && m.tool_calls.length > 0) {
        // Find paired tool result messages that follow, matched by tool_call_id via order
        const calls = m.tool_calls;
        out.push(
          <div key={m.message_id} className="mb-2">
            {calls.map((tc, idx) => {
              // find next tool-role message in the stream for this call (by proximity)
              let result: CaseChatMessage | undefined;
              for (let j = i + 1; j < messages.length; j++) {
                if (messages[j].role === 'tool' && messages[j].tool_name === tc.function.name) {
                  result = messages[j];
                  break;
                }
                if (messages[j].role === 'assistant' || messages[j].role === 'user') break;
              }
              return (
                <ThinkingStep key={tc.id ?? idx} caseId={case_id} toolCall={tc} result={result} isPending={waiting && i === messages.length - 1} />
              );
            })}
          </div>
        );
      } else if (m.role === 'assistant' && m.content) {
        out.push(
          <div key={m.message_id} className="d-flex justify-content-start mb-2">
            <div className="p-2 px-3 rounded border" style={{ background: 'white', maxWidth: '80%', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {m.content}
              <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>{formatTimestamp(m.timestamp)}</div>
            </div>
          </div>
        );
      }
      // 'tool' role messages are rendered inline inside their owning ThinkingStep above, skip standalone.
    }
    return out;
  };

  if (loading) return <div className="py-4 text-center"><Spinner animation="border" /></div>;

  return (
    <div className="mt-3 d-flex flex-column" style={{ height: '65vh' }}>
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <div className="flex-grow-1 p-2 border rounded mb-2" style={{ overflowY: 'auto', background: '#f5f6f8' }}>
        {messages.length === 0 && (
          <div className="text-center text-muted py-5">
            <div style={{ fontSize: '2rem' }}>💬</div>
            <div className="mt-2">Ask a question about this case, or ask for a file/export.</div>
          </div>
        )}
        {renderMessages()}
        {waiting && (
          <div className="d-flex justify-content-start mb-2">
            <div className="p-2 px-3 rounded border text-muted" style={{ background: 'white' }}>
              <Spinner size="sm" animation="border" className="me-2" />Thinking…
              <Badge bg="light" text="dark" className="border ms-2">gpt-5.6</Badge>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <Form onSubmit={handleSend} className="d-flex gap-2">
        <Form.Control
          as="textarea"
          rows={2}
          placeholder="Ask about this case, or ask for a report/export…"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend(e as unknown as React.FormEvent);
            }
          }}
          disabled={waiting}
        />
        <Button type="submit" variant="primary" disabled={waiting || !input.trim()}>
          {waiting ? <Spinner size="sm" animation="border" /> : 'Send'}
        </Button>
      </Form>
    </div>
  );
};

export default CaseChat;
